/**
 * Monta itens e colunas da proposta a partir dos itens do edital,
 * incluindo cabeçalhos de grupo quando houver colunas Grupo/Subgrupo.
 */

import {
  TIPO_LINHA,
  getTipoLinha,
  extrairNumero,
} from "@/utils/propostaItensCalculations";

function strTrim(v) {
  if (v == null || v === "") return "";
  return String(v).trim();
}

/** Primeiro valor não vazio entre várias chaves possíveis (extração legada / sinónimos). */
function primeiroTexto(raw, keys) {
  if (!raw || typeof raw !== "object") return "";
  for (const k of keys) {
    const v = raw[k];
    const t = strTrim(v);
    if (t) return t;
  }
  return "";
}

/**
 * Infere grupo/subgrupo a partir de colunas_itens do edital (ex.: col_0 com nome "Grupo").
 * Ordem: subgrupo antes de grupo no teste do nome para não confundir "Grupo" com "Subgrupo".
 */
export function inferirGrupoSubgrupoPorColunasItens(raw, colunasItens) {
  if (!raw || typeof raw !== "object" || !Array.isArray(colunasItens)) {
    return { grupo: "", subgrupo: "" };
  }
  let grupo = "";
  let subgrupo = "";
  for (const col of colunasItens) {
    const id = col?.id;
    if (id == null || id === "") continue;
    const val = strTrim(raw[id]);
    if (!val) continue;
    const nome = String(col.nome ?? col.label ?? "");
    const nomeId = `${nome} ${id}`;
    if (/subgrupo|sub-grupo|sub\s*grupo|sub_grupo/i.test(nomeId)) {
      subgrupo = val;
      continue;
    }
    if (
      (/\bgrupo\b|grupo\s+de\s+itens|\blote\b|loteamento|\bbloco\b|^grp$/i.test(
        nome
      ) &&
        !/sub/i.test(nome)) ||
      /^grupo$/i.test(String(id)) ||
      /^lote$/i.test(String(id)) ||
      /^grp$/i.test(String(id))
    ) {
      grupo = val;
      continue;
    }
    if (/^subgrupo$/i.test(String(id))) subgrupo = val;
  }
  return { grupo, subgrupo };
}

/** Lê grupo/subgrupo do objeto (snake_case, camelCase, colunas dinâmicas via colunas_itens). */
export function extrairGrupoSubgrupoEdital(raw, colunasItens) {
  if (!raw || typeof raw !== "object") return { grupo: "", subgrupo: "" };
  let grupo = primeiroTexto(raw, [
    "grupo",
    "Grupo",
    "GRUPO",
    "nome_grupo",
    "nomeGrupo",
    "lote",
    "Lote",
    "LOTE",
    "grupo_item",
    "grupoItem",
    "cod_grupo",
    "codGrupo",
  ]);
  let subgrupo = primeiroTexto(raw, [
    "subgrupo",
    "subGrupo",
    "Subgrupo",
    "SUBGRUPO",
    "sub_grupo",
    "subgrupo_item",
    "subGrupoItem",
  ]);
  if (!grupo && !subgrupo && colunasItens) {
    const inf = inferirGrupoSubgrupoPorColunasItens(raw, colunasItens);
    if (inf.grupo) grupo = inf.grupo;
    if (inf.subgrupo) subgrupo = inf.subgrupo;
  }
  return { grupo, subgrupo };
}

/** Forward-fill de grupo/subgrupo (células mescladas na tabela do PDF). */
function aplicarForwardFillGrupoSubgrupo(rows, colunasItens) {
  let lastG = "";
  let lastS = "";
  return rows.map((raw) => {
    const { grupo: rg, subgrupo: rs } = extrairGrupoSubgrupoEdital(raw, colunasItens);
    if (rg) lastG = rg;
    if (rs) lastS = rs;
    return {
      raw,
      grupo: lastG,
      subgrupo: lastS,
    };
  });
}

function chaveGrupo(grupo, subgrupo) {
  return `${grupo}\u0000${subgrupo}`;
}

function tituloGrupo(grupo, subgrupo) {
  if (grupo && subgrupo) return `${grupo} — ${subgrupo}`;
  if (grupo) return grupo;
  if (subgrupo) return subgrupo;
  return "Grupo";
}

const COLUNAS_BASE_PROPOSTA = [
  { id: "item", nome: "Item", tipo: "numero", largura: "w-16", visivel: true, editavel: false },
  { id: "descricao", nome: "Descrição", tipo: "texto_longo", largura: "min-w-[400px]", visivel: true, editavel: true },
  { id: "unidade", nome: "Unidade", tipo: "texto", largura: "w-24", visivel: true, editavel: true },
  { id: "quantidade", nome: "Quantidade", tipo: "numero", largura: "w-32", visivel: true, editavel: true },
  { id: "valor_unitario", nome: "Valor Unit.", tipo: "valor", largura: "w-40", visivel: true, editavel: true },
];

function colunasComGrupoSubgrupo() {
  const g = {
    id: "grupo",
    nome: "Grupo",
    tipo: "texto",
    largura: "w-36",
    visivel: true,
    editavel: true,
  };
  const sg = {
    id: "subgrupo",
    nome: "Subgrupo",
    tipo: "texto",
    largura: "w-36",
    visivel: true,
    editavel: true,
  };
  const [item, ...rest] = COLUNAS_BASE_PROPOSTA;
  return [item, g, sg, ...rest];
}

/** Copia células dinâmicas do edital (col_0, col_1, …) para a linha da proposta. */
export function extrasColDinamicasEdital(raw) {
  if (!raw || typeof raw !== "object") return {};
  const o = {};
  for (const k of Object.keys(raw)) {
    if (k.startsWith("col_")) o[k] = raw[k];
  }
  return o;
}

function linhaDadosPlana(raw, colunasItens = null) {
  const { valor_unitario, valor_total } = valoresReferenciaParaProposta(
    raw,
    colunasItens
  );
  return {
    ...extrasColDinamicasEdital(raw),
    item: raw?.item,
    descricao: raw?.descricao ?? "",
    unidade: raw?.unidade ?? "",
    quantidade: primeiroNumeroDefinido(raw?.quantidade, raw?.Quantidade),
    valor_unitario,
    valor_total,
  };
}

/** Colunas: Grupo/Subgrupo + colunas_itens do edital (incl. col_*), ou tabela padrão com grupo. */
export function buildColunasPropostaParaEdital(editalColunas) {
  if (editalColunas && Array.isArray(editalColunas) && editalColunas.length > 0) {
    const ids = new Set(editalColunas.map((c) => c?.id).filter(Boolean));
    const prefix = [];
    if (!ids.has("grupo")) {
      prefix.push({
        id: "grupo",
        nome: "Grupo",
        tipo: "texto",
        largura: "w-36",
        visivel: true,
        editavel: true,
      });
    }
    if (!ids.has("subgrupo")) {
      prefix.push({
        id: "subgrupo",
        nome: "Subgrupo",
        tipo: "texto",
        largura: "w-36",
        visivel: true,
        editavel: true,
      });
    }
    return [
      ...prefix,
      ...editalColunas.map((c) => ({
        largura: c.largura || "min-w-[120px]",
        visivel: c.visivel !== false,
        editavel: c.editavel !== false,
        ...c,
      })),
    ];
  }
  return colunasComGrupoSubgrupo();
}

function inferirColunasMinimasColStar(lista) {
  const keys = new Set();
  for (const row of lista) {
    if (!row || typeof row !== "object") continue;
    for (const k of Object.keys(row)) {
      if (k.startsWith("col_")) keys.add(k);
    }
  }
  const sorted = [...keys].sort(
    (a, b) =>
      parseInt(String(a).replace(/\D/g, ""), 10) -
      parseInt(String(b).replace(/\D/g, ""), 10)
  );
  return sorted.map((id, i) => ({
    id,
    nome: `Coluna ${i + 1}`,
    tipo: "texto",
    largura: "min-w-[120px]",
    visivel: true,
    editavel: true,
  }));
}

function asArrayItens(v) {
  if (Array.isArray(v)) return v;
  if (typeof v === "string" && v.trim()) {
    try {
      const p = JSON.parse(v);
      return Array.isArray(p) ? p : [];
    } catch {
      return [];
    }
  }
  return [];
}

/** Converte valor de campo "item" para número (ordenação); NaN se não for identificável. */
function parseNumeroItemFlex(v) {
  if (v == null || v === "") return NaN;
  if (typeof v === "number" && Number.isFinite(v)) return v;
  const s = String(v).trim().replace(/\s/g, "");
  const m = s.match(/^(\d+)/);
  if (m) return parseInt(m[1], 10);
  const n = parseFloat(String(s).replace(",", "."));
  return Number.isFinite(n) ? n : NaN;
}

/**
 * Obtém o número do item para ordenar a lista na mesma ordem do edital na UI (por Nº item).
 * Usa campos diretos e, com colunas_itens, colunas nomeadas "Item" / col_*.
 */
export function extrairNumeroItemParaOrdenacao(raw, colunasItens) {
  if (!raw || typeof raw !== "object") return NaN;
  const chavesDiretas = [
    "item",
    "Item",
    "numero_item",
    "numeroItem",
    "ordem",
    "Ordem",
    "numero",
    "Numero",
  ];
  for (const k of chavesDiretas) {
    const n = parseNumeroItemFlex(raw[k]);
    if (!Number.isNaN(n)) return n;
  }
  if (Array.isArray(colunasItens)) {
    for (const col of colunasItens) {
      const id = col?.id;
      if (id == null || id === "") continue;
      const nome = String(col.nome ?? col.label ?? "");
      const ehItem =
        /^item$/i.test(String(id)) ||
        /^n[º°]?\s*item/i.test(nome) ||
        (/^item$/i.test(nome) && !/sub/i.test(nome));
      if (ehItem) {
        const n = parseNumeroItemFlex(raw[id]);
        if (!Number.isNaN(n)) return n;
      }
    }
    for (const col of colunasItens) {
      const id = col?.id;
      if (!id || !String(id).startsWith("col_")) continue;
      const nome = String(col.nome ?? col.label ?? "");
      if (/\bitem\b/i.test(nome) && !/subgrupo/i.test(nome)) {
        const n = parseNumeroItemFlex(raw[id]);
        if (!Number.isNaN(n)) return n;
      }
    }
  }
  return NaN;
}

/**
 * Valor do nº do item para o campo `item` da proposta (canónico ou coluna dinâmica em colunas_itens).
 */
function extrairValorItemEditalParaCampo(raw, colunasItens) {
  if (!raw || typeof raw !== "object") return null;
  for (const k of ["item", "Item", "numero_item", "numeroItem"]) {
    const v = raw[k];
    if (v != null && String(v).trim() !== "") {
      const n = parseNumeroItemFlex(v);
      return Number.isNaN(n) ? v : n;
    }
  }
  if (!Array.isArray(colunasItens)) return null;
  for (const col of colunasItens) {
    const id = col?.id;
    if (id == null || id === "") continue;
    const nome = String(col.nome ?? col.label ?? "");
    const ehItem =
      /^item$/i.test(String(id)) ||
      /^n[º°]?\s*item/i.test(nome) ||
      (/^item$/i.test(nome) && !/sub/i.test(nome));
    if (ehItem) {
      const v = raw[id];
      if (v != null && String(v).trim() !== "") {
        const n = parseNumeroItemFlex(v);
        return Number.isNaN(n) ? v : n;
      }
    }
  }
  for (const col of colunasItens) {
    const id = col?.id;
    if (!id || !String(id).startsWith("col_")) continue;
    const nome = String(col.nome ?? col.label ?? "");
    if (/\bitem\b/i.test(nome) && !/subgrupo/i.test(nome)) {
      const v = raw[id];
      if (v != null && String(v).trim() !== "") {
        const n = parseNumeroItemFlex(v);
        return Number.isNaN(n) ? v : n;
      }
    }
  }
  return null;
}

/**
 * Preenche `item`, `grupo` e `subgrupo` quando a extração só os guardou em col_* / colunas_itens.
 */
function enriquecerCamposCanonicosEdital(raw, colunasItens) {
  if (!raw || typeof raw !== "object") return raw;
  const vItem = extrairValorItemEditalParaCampo(raw, colunasItens);
  const { grupo: gInf, subgrupo: sInf } = extrairGrupoSubgrupoEdital(
    raw,
    colunasItens
  );
  const out = { ...raw };
  if (vItem != null && (out.item == null || String(out.item).trim() === "")) {
    out.item = vItem;
  }
  if (!strTrim(out.grupo) && gInf) out.grupo = gInf;
  if (!strTrim(out.subgrupo) && sInf) out.subgrupo = sInf;
  return out;
}

/** Replica o título do cabeçalho de grupo (ex.: "01") na coluna Grupo quando a linha não tem grupo. */
function preencherGrupoComTituloCabecalhoAnterior(itens) {
  let tituloAtual = "";
  return (itens || []).map((row) => {
    const t = getTipoLinha(row);
    if (t === TIPO_LINHA.CABECALHO_GRUPO) {
      tituloAtual = strTrim(row.titulo_grupo) || tituloAtual;
      return row;
    }
    if (t === TIPO_LINHA.DADOS && !strTrim(row.grupo) && tituloAtual) {
      return { ...row, grupo: tituloAtual };
    }
    return row;
  });
}

/** Garante nº de item em linhas de dados quando ainda vazio (sequência após o maior já definido). */
function preencherItemOndeVazio(itens) {
  let maxItem = 0;
  for (const row of itens || []) {
    if (getTipoLinha(row) !== TIPO_LINHA.DADOS) continue;
    const n = parseNumeroItemFlex(row.item);
    if (!Number.isNaN(n)) maxItem = Math.max(maxItem, n);
  }
  let next = maxItem + 1;
  return (itens || []).map((row) => {
    if (getTipoLinha(row) !== TIPO_LINHA.DADOS) return row;
    const empty = row.item == null || String(row.item).trim() === "";
    if (!empty) return row;
    return { ...row, item: next++ };
  });
}

function finalizarLinhasProposta(itens) {
  if (!Array.isArray(itens) || itens.length === 0) return itens;
  return preencherItemOndeVazio(preencherGrupoComTituloCabecalhoAnterior(itens));
}

/**
 * Classifica coluna da proposta (ids fixos ou col_* do edital) para sincronizar com campos canónicos na linha.
 * @returns {'item'|'grupo'|'subgrupo'|'descricao'|'quantidade'|'unidade'|'valor_unitario'|'valor_total'|null}
 */
function classificarColunaProposta(col) {
  if (!col || typeof col !== "object") return null;
  const id = String(col.id ?? "");
  const nome = String(col.nome ?? col.label ?? "").toLowerCase();
  const nomeId = `${nome} ${id}`;

  if (/subgrupo|sub-grupo|sub\s*grupo|sub_grupo/i.test(nomeId) || /^subgrupo$/i.test(id))
    return "subgrupo";
  if (
    id === "grupo" ||
    ((/\bgrupo\b|loteamento|\blote\b/i.test(nome)) && !/subgrupo/i.test(nomeId)) ||
    /^lote$/i.test(id) ||
    /^grp$/i.test(id)
  )
    return "grupo";

  if (
    /^item$/i.test(id) ||
    /^n[º°]?\s*item/i.test(nome) ||
    (/^item$/i.test(nome) && !/sub/i.test(nome))
  )
    return "item";

  if (id === "descricao" || /\bdescri/i.test(nome) || /\bespecifica/i.test(nome))
    return "descricao";
  if (id === "quantidade" || (/\bquantidade\b/i.test(nome) && !/valor/i.test(nome)))
    return "quantidade";
  if (id === "unidade" || /\bund\b/i.test(nome) || /\bunidade\b/i.test(nome)) return "unidade";

  if (
    id === "valor_unitario" ||
    id === "valor_referencia" ||
    id === "valorUnitario" ||
    id === "valorReferencia" ||
    /\bvalor\s*unit/i.test(nome) ||
    /\bpre[cç]o\s*unit/i.test(nome) ||
    /\bv\.?\s*unit\b/i.test(nome)
  )
    return "valor_unitario";
  if (id === "valor_total" || /\bvalor\s*total\b/i.test(nome)) return "valor_total";

  return null;
}

function vazioParaSincronia(val, semantica) {
  if (val == null) return true;
  if (
    semantica === "descricao" ||
    semantica === "grupo" ||
    semantica === "subgrupo" ||
    semantica === "unidade"
  ) {
    return strTrim(String(val)) === "";
  }
  if (semantica === "item") {
    if (typeof val === "string" && strTrim(val) === "") return true;
    return Number.isNaN(parseNumeroItemFlex(val));
  }
  if (
    semantica === "quantidade" ||
    semantica === "valor_unitario" ||
    semantica === "valor_total"
  ) {
    return extrairNumero(val) === 0;
  }
  return false;
}

function obterCanonico(row, sem) {
  switch (sem) {
    case "item":
      return row.item;
    case "grupo":
      return row.grupo;
    case "subgrupo":
      return row.subgrupo;
    case "descricao":
      return row.descricao;
    case "quantidade":
      return row.quantidade;
    case "unidade":
      return row.unidade;
    case "valor_unitario":
      return row.valor_unitario ?? row.valor_referencia;
    case "valor_total":
      return row.valor_total;
    default:
      return undefined;
  }
}

function definirCanonico(row, sem, val) {
  const out = { ...row };
  switch (sem) {
    case "item": {
      const n = parseNumeroItemFlex(val);
      out.item = Number.isNaN(n) ? val : n;
      break;
    }
    case "grupo":
      out.grupo = strTrim(String(val));
      break;
    case "subgrupo":
      out.subgrupo = strTrim(String(val));
      break;
    case "descricao":
      out.descricao = String(val ?? "");
      break;
    case "quantidade":
      out.quantidade = extrairNumero(val);
      break;
    case "unidade":
      out.unidade = strTrim(String(val));
      break;
    case "valor_unitario": {
      const n = extrairNumero(val);
      out.valor_unitario = n;
      break;
    }
    case "valor_total":
      out.valor_total = extrairNumero(val);
      break;
  }
  return out;
}

/**
 * A grelha lê `linha[coluna.id]`; o edital muitas vezes só preenche campos canónicos ou só `col_*`.
 * Sincroniza nos dois sentidos para a mesma coluna semântica.
 */
function sincronizarLinhaDadosComColunasProposta(row, colunasProposta) {
  if (!row || !Array.isArray(colunasProposta) || colunasProposta.length === 0)
    return row;
  let r = { ...row };

  for (const col of colunasProposta) {
    const cid = col?.id;
    if (cid == null || cid === "") continue;
    const sem = classificarColunaProposta(col);
    if (!sem) continue;

    const celula = r[cid];
    const canonVal = obterCanonico(r, sem);

    const celVazio = vazioParaSincronia(celula, sem);
    const canVazio = vazioParaSincronia(canonVal, sem);

    if (celVazio && !canVazio) {
      r[cid] = canonVal;
    } else if (canVazio && !celVazio) {
      r = definirCanonico(r, sem, celula);
    }
  }

  return r;
}

function concluirItensProposta(itens, colunas_itens) {
  const base = finalizarLinhasProposta(itens);
  if (!Array.isArray(colunas_itens) || colunas_itens.length === 0) return base;
  return base.map((row) => {
    if (getTipoLinha(row) !== TIPO_LINHA.DADOS) return row;
    return sincronizarLinhaDadosComColunasProposta(row, colunas_itens);
  });
}

/**
 * Ordem estável: itens com número primeiro (crescente); sem número mantêm a ordem original no fim.
 */
function ordenarItensEditalPorNumeroItem(lista, colunasItens) {
  if (!Array.isArray(lista) || lista.length < 2) return lista;
  const comIdx = lista.map((raw, idx) => ({ raw, idx }));
  comIdx.sort((a, b) => {
    const na = extrairNumeroItemParaOrdenacao(a.raw, colunasItens);
    const nb = extrairNumeroItemParaOrdenacao(b.raw, colunasItens);
    const aOk = !Number.isNaN(na);
    const bOk = !Number.isNaN(nb);
    if (aOk && bOk && na !== nb) return na - nb;
    if (aOk && !bOk) return -1;
    if (!aOk && bOk) return 1;
    return a.idx - b.idx;
  });
  return comIdx.map((x) => x.raw);
}

/**
 * Itens gravados em Itens da Licitação: cabeçalhos/subtotais/grupo_id.
 * Lista plana de extração (só linhas de dados com grupo em coluna) retorna false.
 */
export function isItensEditalEstruturados(itens) {
  if (!Array.isArray(itens) || itens.length === 0) return false;
  for (const row of itens) {
    if (!row || typeof row !== "object") continue;
    const t = getTipoLinha(row);
    if (t === TIPO_LINHA.CABECALHO_GRUPO || t === TIPO_LINHA.SUBTOTAL) {
      return true;
    }
    if (
      t === TIPO_LINHA.DADOS &&
      row.grupo_id != null &&
      String(row.grupo_id).trim() !== ""
    ) {
      return true;
    }
  }
  return false;
}

/** Colunas da licitação (valor referência) → colunas da proposta (valor unitário fornecedor). */
function colunasEditalParaPropostaColunas(colunasEdital) {
  if (!colunasEdital || colunasEdital.length === 0) return null;
  return colunasEdital.map((c) => {
    if (c?.id === "valor_referencia" || c?.id === "valorReferencia") {
      return {
        ...c,
        id: "valor_unitario",
        nome: "Valor Unit.",
        editavel: c.editavel !== false,
      };
    }
    return { ...c };
  });
}

/** Ordem alinhada à Licitação quando o edital não traz colunas_itens. */
function colunasPropostaFallbackEstruturado() {
  return [
    {
      id: "item",
      nome: "Item",
      tipo: "numero",
      largura: "w-16",
      visivel: true,
      editavel: false,
    },
    {
      id: "descricao",
      nome: "Descrição",
      tipo: "texto_longo",
      largura: "min-w-[400px]",
      visivel: true,
      editavel: true,
    },
    {
      id: "quantidade",
      nome: "Quantidade",
      tipo: "numero",
      largura: "w-32",
      visivel: true,
      editavel: true,
    },
    {
      id: "unidade",
      nome: "Unidade",
      tipo: "texto",
      largura: "w-24",
      visivel: true,
      editavel: true,
    },
    {
      id: "valor_unitario",
      nome: "Valor Unit.",
      tipo: "valor",
      largura: "w-40",
      visivel: true,
      editavel: true,
    },
    {
      id: "valor_total",
      nome: "Valor Total",
      tipo: "valor",
      largura: "w-40",
      visivel: true,
      editavel: true,
    },
  ];
}

function primeiroNumeroDefinido(...vals) {
  for (const v of vals) {
    if (v == null || v === "") continue;
    return extrairNumero(v);
  }
  return 0;
}

function primeiroTextoDefinido(...vals) {
  for (const v of vals) {
    const s = strTrim(v);
    if (s) return s;
  }
  return "";
}

function primeiroValorDefinido(...vals) {
  for (const v of vals) {
    if (v == null || v === "") continue;
    return v;
  }
  return undefined;
}

/**
 * API / JSON podem devolver camelCase; Licitação usa snake_case nos itens.
 */
function normalizarLinhaDadosEditalBruto(row) {
  if (!row || typeof row !== "object") return row;
  return {
    ...row,
    quantidade:
      row.quantidade ??
      row.Quantidade ??
      row.quantidade_item,
    unidade: primeiroTextoDefinido(row.unidade, row.Unidade, row.unid),
    descricao: primeiroTextoDefinido(
      row.descricao,
      row.Descricao,
      row.descricao_item
    ),
    item: row.item ?? row.Item ?? row.numero_item ?? row.numeroItem,
    valor_referencia: primeiroValorDefinido(
      row.valor_referencia,
      row.valorReferencia,
      row.valor_unitario_edital
    ),
    valor_total: row.valor_total ?? row.valorTotal,
    valor_unitario: row.valor_unitario ?? row.valorUnitario,
  };
}

function primeiroValorUnitarioEmColunasItens(raw, colunasItens) {
  if (!raw || !Array.isArray(colunasItens)) return null;
  for (const col of colunasItens) {
    if (classificarColunaProposta(col) !== "valor_unitario") continue;
    const id = col?.id;
    if (id == null || id === "") continue;
    const v = raw[id];
    if (v != null && strTrim(String(v)) !== "") return extrairNumero(v);
  }
  return null;
}

function primeiroValorTotalEmColunasItens(raw, colunasItens) {
  if (!raw || !Array.isArray(colunasItens)) return null;
  for (const col of colunasItens) {
    if (classificarColunaProposta(col) !== "valor_total") continue;
    const id = col?.id;
    if (id == null || id === "") continue;
    const v = raw[id];
    if (v != null && strTrim(String(v)) !== "") return extrairNumero(v);
  }
  return null;
}

/** Valores unitário/total iniciais a partir do edital (referência / planilha / colunas dinâmicas). */
function valoresReferenciaParaProposta(raw, colunasItens = null) {
  const nu = normalizarLinhaDadosEditalBruto(raw);
  const q = primeiroNumeroDefinido(nu.quantidade);
  const refUnit = nu.valor_referencia;
  let vuFromRef =
    refUnit != null && String(refUnit).trim() !== ""
      ? extrairNumero(refUnit)
      : primeiroNumeroDefinido(nu.valor_unitario);
  if (vuFromRef === 0 && colunasItens) {
    const vCol = primeiroValorUnitarioEmColunasItens(raw, colunasItens);
    if (vCol != null && vCol !== 0) vuFromRef = vCol;
  }
  let vt = primeiroNumeroDefinido(nu.valor_total);
  if (vt === 0 && colunasItens) {
    const vtCol = primeiroValorTotalEmColunasItens(raw, colunasItens);
    if (vtCol != null && vtCol !== 0) vt = vtCol;
  }
  if (vt === 0 && q !== 0 && vuFromRef !== 0) {
    vt = q * vuFromRef;
  }
  return { valor_unitario: vuFromRef, valor_total: vt };
}

function linhaDadosEstruturadaParaProposta(row, colunasItens = null) {
  const nu = normalizarLinhaDadosEditalBruto(row);
  const {
    valor_referencia: _omitRef,
    tipo_linha: _omitTipo,
    ...rest
  } = nu;

  const q = primeiroNumeroDefinido(nu.quantidade);
  const un = primeiroTextoDefinido(nu.unidade);
  const { valor_unitario: vuFromRef, valor_total: vt } =
    valoresReferenciaParaProposta(row, colunasItens);

  return {
    ...extrasColDinamicasEdital(nu),
    ...rest,
    tipo_linha: TIPO_LINHA.DADOS,
    quantidade: q,
    unidade: un,
    descricao: rest.descricao ?? nu.descricao ?? "",
    valor_unitario: vuFromRef,
    valor_total: vt,
  };
}

/**
 * Preserva ordem, cabeçalhos, subtotais e grupo_id vindos de Itens da Licitação.
 */
function buildPropostaItensFromEditalEstruturado(lista, options = {}) {
  const colunasItens =
    options.colunasItens ?? options.colunas_itens ?? null;
  const out = [];

  for (const row of lista) {
    if (!row || typeof row !== "object") continue;
    const t = getTipoLinha(row);

    if (t === TIPO_LINHA.CABECALHO_GRUPO) {
      out.push({
        tipo_linha: TIPO_LINHA.CABECALHO_GRUPO,
        grupo_id: row.grupo_id,
        titulo_grupo: strTrim(row.titulo_grupo) || "Grupo",
      });
      continue;
    }

    if (t === TIPO_LINHA.SUBTOTAL) {
      out.push({
        tipo_linha: TIPO_LINHA.SUBTOTAL,
        grupo_id: row.grupo_id,
      });
      continue;
    }

    if (t === TIPO_LINHA.DADOS) {
      out.push(linhaDadosEstruturadaParaProposta(row, colunasItens));
      continue;
    }

    out.push(linhaDadosEstruturadaParaProposta(row, colunasItens));
  }

  const colsMapeadas = colunasEditalParaPropostaColunas(colunasItens);
  const colunasFinal =
    colsMapeadas && colsMapeadas.length > 0
      ? colsMapeadas
      : colunasPropostaFallbackEstruturado();

  return {
    itens: out,
    colunas_itens: colunasFinal,
  };
}

/**
 * @param {Array<Record<string, unknown>>} editalItens
 * @param {{ colunasItens?: Array<Record<string, unknown>> | null, ordenarPorNumeroItem?: boolean }} [options]
 * @returns {{ itens: Array<Record<string, unknown>>, colunas_itens: Array<Record<string, unknown>> | null }}
 */
export function buildPropostaItensFromEditalItens(editalItens, options = {}) {
  const colunasItens =
    options.colunasItens ??
    options.colunas_itens ??
    null;
  let lista = asArrayItens(editalItens);
  if (lista.length === 0) {
    return { itens: [], colunas_itens: null };
  }

  lista = lista.map((raw) => {
    if (!raw || typeof raw !== "object") return raw;
    if (getTipoLinha(raw) !== TIPO_LINHA.DADOS) return raw;
    return enriquecerCamposCanonicosEdital(raw, colunasItens);
  });

  if (isItensEditalEstruturados(lista)) {
    const r = buildPropostaItensFromEditalEstruturado(lista, options);
    return {
      ...r,
      itens: concluirItensProposta(r.itens, r.colunas_itens),
    };
  }

  if (options.ordenarPorNumeroItem !== false) {
    lista = ordenarItensEditalPorNumeroItem(lista, colunasItens);
  }

  const comFill = aplicarForwardFillGrupoSubgrupo(lista, colunasItens);
  const temAlgumGrupo = comFill.some(
    (r) => (r.grupo && r.grupo.length > 0) || (r.subgrupo && r.subgrupo.length > 0)
  );

  /**
   * Nova Proposta de Preço: mostrar sempre estrutura com Grupo + Subtotal quando há itens.
   * Se o edital não trouxer grupo/subgrupo, usa um único bloco "Itens do pregão" para não ficar lista plana.
   */
  const forcarEstrutura =
    options.sempreEstruturarGrupoSubtotal !== false;

  if (!temAlgumGrupo) {
    if (!forcarEstrutura || lista.length === 0) {
      const temColStar = lista.some((r) =>
        Object.keys(r || {}).some((k) => k.startsWith("col_"))
      );
      let colunasFlat = null;
      if (temColStar) {
        colunasFlat =
          colunasItens && colunasItens.length > 0
            ? buildColunasPropostaParaEdital(colunasItens)
            : buildColunasPropostaParaEdital(inferirColunasMinimasColStar(lista));
      }
      return {
        itens: concluirItensProposta(
          lista.map((raw) => linhaDadosPlana(raw, colunasItens)),
          colunasFlat
        ),
        colunas_itens: colunasFlat,
      };
    }
    const gid = `grupo_${Date.now()}_0_${Math.random().toString(36).slice(2, 9)}`;
    const titulo =
      options.tituloGrupoUnico && String(options.tituloGrupoUnico).trim()
        ? String(options.tituloGrupoUnico).trim()
        : "Itens do pregão";
    const itensUnicoGrupo = [
      {
        tipo_linha: TIPO_LINHA.CABECALHO_GRUPO,
        grupo_id: gid,
        titulo_grupo: titulo,
      },
      ...lista.map((raw) => {
        const v = valoresReferenciaParaProposta(raw, colunasItens);
        return {
          ...extrasColDinamicasEdital(raw),
          tipo_linha: TIPO_LINHA.DADOS,
          grupo_id: gid,
          item: raw?.item,
          descricao: raw?.descricao ?? "",
          unidade: raw?.unidade ?? "",
          quantidade: raw?.quantidade ?? 0,
          valor_unitario: v.valor_unitario,
          valor_total: v.valor_total,
          grupo: strTrim(raw?.grupo) || "",
          subgrupo: strTrim(raw?.subgrupo) || "",
        };
      }),
      {
        tipo_linha: TIPO_LINHA.SUBTOTAL,
        grupo_id: gid,
      },
    ];
    const colsUnico = buildColunasPropostaParaEdital(colunasItens);
    return {
      itens: concluirItensProposta(itensUnicoGrupo, colsUnico),
      colunas_itens: colsUnico,
    };
  }

  const out = [];
  let prevKey = null;
  let grupoIdAtual = null;

  for (const { raw, grupo, subgrupo } of comFill) {
    const key = chaveGrupo(grupo, subgrupo);
    if (key !== prevKey) {
      if (prevKey !== null && grupoIdAtual) {
        out.push({
          tipo_linha: TIPO_LINHA.SUBTOTAL,
          grupo_id: grupoIdAtual,
        });
      }
      prevKey = key;
      grupoIdAtual = `grupo_${Date.now()}_${out.length}_${Math.random().toString(36).slice(2, 9)}`;
      out.push({
        tipo_linha: TIPO_LINHA.CABECALHO_GRUPO,
        grupo_id: grupoIdAtual,
        titulo_grupo: tituloGrupo(grupo, subgrupo),
      });
    }

    const vRef = valoresReferenciaParaProposta(raw, colunasItens);
    out.push({
      ...extrasColDinamicasEdital(raw),
      tipo_linha: TIPO_LINHA.DADOS,
      grupo_id: grupoIdAtual,
      item: raw?.item,
      descricao: raw?.descricao ?? "",
      unidade: raw?.unidade ?? "",
      quantidade: raw?.quantidade ?? 0,
      valor_unitario: vRef.valor_unitario,
      valor_total: vRef.valor_total,
      grupo,
      subgrupo,
    });
  }

  if (grupoIdAtual) {
    out.push({
      tipo_linha: TIPO_LINHA.SUBTOTAL,
      grupo_id: grupoIdAtual,
    });
  }

  const colsMulti = buildColunasPropostaParaEdital(colunasItens);
  return {
    itens: concluirItensProposta(out, colsMulti),
    colunas_itens: colsMulti,
  };
}
